<template>
  <div class="crewing-timeline w-full">
    <!-- Toolbar -->
    <div class="bg-white p-4 rounded-lg shadow mb-4">
      <div class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <h2 class="text-xl font-semibold text-gray-800">
            Crew timeline
          </h2>
          <div class="flex flex-wrap items-center gap-3">
            <div class="flex items-center gap-2 border border-gray-300 dark:border-surface-600 rounded-md px-3 py-1.5">
              <InputSwitch
                v-model="hideCompleteProjects"
                input-id="hide-complete"
              />
              <label
                for="hide-complete"
                class="text-sm text-gray-700 dark:text-surface-300 cursor-pointer select-none"
              >
                Hide Complete projects
              </label>
            </div>
            <div
              class="flex rounded-md overflow-hidden border border-gray-300 dark:border-surface-600"
              role="group"
              aria-label="View mode"
            >
              <Button
                v-for="opt in viewOptions"
                :key="opt.value"
                :label="opt.label"
                :severity="viewMode === opt.value ? 'primary' : 'secondary'"
                :outlined="viewMode !== opt.value"
                class="rounded-none border-r last:border-r-0 border-gray-300 dark:border-surface-600"
                @click="viewMode = opt.value; onViewModeChange()"
              />
            </div>
          </div>
        </div>
        <p class="text-sm text-surface-600 dark:text-surface-400">
          {{ viewRangeLabel }}
        </p>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="loading"
      class="bg-white rounded-lg shadow p-8 flex flex-col items-center justify-center gap-3"
    >
      <ProgressSpinner style="width: 48px; height: 48px" />
      <span class="text-surface-600">Loading projects…</span>
    </div>

    <!-- Error -->
    <div
      v-else-if="error"
      class="bg-white rounded-lg shadow p-6"
    >
      <Message severity="error">
        {{ error }}
      </Message>
      <Button
        label="Retry"
        icon="pi pi-refresh"
        class="mt-3"
        @click="loadProjects"
      />
    </div>

    <!-- Month / Week view: same timeline layout as projects (status bar + crew phases glued to bottom) -->
    <TimelineView
      v-else-if="viewMode === 'month' || viewMode === 'week'"
      :rows="timelineViewRows"
      :calendar-start="calendarStart"
      :view-mode="viewMode"
      :month-view-days="MONTH_VIEW_DAYS"
      empty-message="No projects in this period"
      @update:calendar-start="onCalendarUpdate"
      @row-click="goToProject"
    />

    <!-- Day view -->
    <div
      v-else-if="viewMode === 'day'"
      class="bg-white rounded-lg shadow overflow-hidden"
    >
      <div class="p-4 border-b flex items-center justify-center gap-2">
        <Button
          icon="pi pi-chevron-left"
          text
          rounded
          size="small"
          aria-label="Previous day"
          @click="goBackward"
        />
        <h3 class="text-lg font-semibold text-gray-800 min-w-[280px] text-center">
          Day view – {{ formatDayFull(dayViewDate) }}
        </h3>
        <Button
          icon="pi pi-chevron-right"
          text
          rounded
          size="small"
          aria-label="Next day"
          @click="goForward"
        />
        <Button
          icon="pi pi-calendar"
          text
          rounded
          size="small"
          aria-label="Go to today"
          class="ml-2"
          @click="goToToday"
        />
      </div>
      <div class="p-4">
        <div
          v-for="project in getProjectsForDay(dayViewDate)"
          :key="project.id"
          class="crew-project-card p-4 rounded-lg border mb-3 cursor-pointer hover:shadow-md transition-shadow flex flex-wrap items-center justify-between gap-4"
          @click="goToProject(project.id)"
        >
          <div class="font-semibold text-gray-900">
            {{ project.name }}
          </div>
          <div class="flex flex-wrap gap-3">
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-500">Unassigned</span>
              <Tag
                :value="String(project.unassigned)"
                severity="danger"
              />
            </div>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-500">Outstanding</span>
              <Tag
                :value="String(project.outstanding)"
                severity="warn"
              />
            </div>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-500">Confirmed</span>
              <Tag
                :value="String(project.confirmed)"
                severity="success"
              />
            </div>
          </div>
        </div>
        <div
          v-if="getProjectsForDay(dayViewDate).length === 0"
          class="text-center py-12 text-surface-500"
        >
          <i class="pi pi-calendar text-4xl mb-2 block opacity-50" />
          No projects on this day
        </div>
      </div>
    </div>

    <!-- Fallback if viewMode is unexpected -->
    <div
      v-else
      class="bg-white rounded-lg shadow p-6"
    >
      <p class="text-surface-600 mb-3">
        Select a view: Month, Week, or Day.
      </p>
      <Button
        label="Month"
        class="mr-2"
        @click="viewMode = 'month'"
      />
      <Button
        label="Week"
        class="mr-2"
        @click="viewMode = 'week'"
      />
      <Button
        label="Day"
        @click="viewMode = 'day'"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import {
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  startOfWeek,
  endOfWeek,
  startOfDay,
  format,
  isSameDay,
  parseISO,
  differenceInDays
} from 'date-fns';
import { getCrewTimelineProjects } from '@/services/crewingTimelineService';
import TimelineView from '@/shared/components/TimelineView.vue';

const router = useRouter();

const viewOptions = [
  { label: 'Month', value: 'month' },
  { label: 'Week', value: 'week' },
  { label: 'Day', value: 'day' }
];

const MONTH_VIEW_DAYS = 30; // Month view shows a continuous 30-day window (not calendar month)
const viewMode = ref('month');
const calendarStart = ref(new Date()); // month: start of 30-day window; week: start of week; day: the day
const projects = ref([]);
const loading = ref(true);
const error = ref(null);
const hideCompleteProjects = ref(false);

/** Projects to display: when hideCompleteProjects is on, filter out projects with unassigned === 0. */
const displayedProjects = computed(() => {
  const list = projects.value;
  if (!hideCompleteProjects.value) return list;
  return list.filter((p) => (p.unassigned ?? 0) > 0);
});

function parseProjectDate(val) {
  if (!val) return null;
  const d = typeof val === 'string' ? parseISO(val.split('T')[0]) : new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
}

function projectSpansDate(project, date) {
  const start = parseProjectDate(project.start_date);
  const end = parseProjectDate(project.end_date);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  if (!start) return false;
  const startOnly = new Date(start);
  startOnly.setHours(0, 0, 0, 0);
  if (d < startOnly) return false;
  if (!end) return isSameDay(start, date);
  const endOnly = new Date(end);
  endOnly.setHours(0, 0, 0, 0);
  return d <= endOnly;
}

const getProjectsForDay = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return displayedProjects.value.filter((p) => projectSpansDate(p, d));
};

const dayViewDate = computed(() => {
  if (viewMode.value === 'day') return calendarStart.value;
  return calendarStart.value;
});

// eslint-disable-next-line no-unused-vars -- reserved for week view UI
const weekDays = computed(() => {
  if (viewMode.value !== 'week') return [];
  const start = startOfWeek(calendarStart.value, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(start, i);
    return {
      key: format(date, 'yyyy-MM-dd'),
      date,
      dayNum: format(date, 'd'),
      isToday: isSameDay(date, new Date())
    };
  });
});

/** Crew status bar style: red if unassigned, amber if outstanding, else green (confirmed). */
function getCrewBarStyle(project) {
  const u = project.unassigned ?? 0;
  const o = project.outstanding ?? 0;
  const border = { borderWidth: '1px', borderStyle: 'solid' };
  if (u > 0) return { backgroundColor: '#fee2e2', borderColor: '#f87171', ...border };
  if (o > 0) return { backgroundColor: '#fef3c7', borderColor: '#f59e0b', ...border };
  return { backgroundColor: '#d1fae5', borderColor: '#34d399', ...border };
}

/** Phase-like segments for crew: Unassigned (red), Outstanding (amber), Confirmed (green) proportional to counts. */
function getCrewPhaseBars(project) {
  const u = project.unassigned ?? 0;
  const o = project.outstanding ?? 0;
  const c = project.confirmed ?? 0;
  const total = u + o + c;
  if (total === 0) {
    return [{ leftPct: 0, widthPct: 100, color: '#9ca3af', tooltip: 'No crew', label: 'No crew' }];
  }
  const uPct = (u / total) * 100;
  const oPct = (o / total) * 100;
  const cPct = (c / total) * 100;
  const bars = [];
  if (uPct > 0) bars.push({ leftPct: 0, widthPct: uPct, color: '#ef4444', tooltip: `Unassigned: ${u}`, label: 'Unassigned' });
  if (oPct > 0) bars.push({ leftPct: uPct, widthPct: oPct, color: '#f59e0b', tooltip: `Outstanding: ${o}`, label: 'Outstanding' });
  if (cPct > 0) bars.push({ leftPct: uPct + oPct, widthPct: cPct, color: '#10b981', tooltip: `Confirmed: ${c}`, label: 'Confirmed' });
  return bars;
}

/** Linear week view: projects that overlap the week, with leftPct and widthPct for bar position (same format as month). */
const linearWeekProjectRows = computed(() => {
  if (viewMode.value !== 'week') return [];
  const weekStart = startOfWeek(calendarStart.value, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(calendarStart.value, { weekStartsOn: 1 });
  const totalDays = 7;
  const rows = [];
  for (const project of displayedProjects.value) {
    const pStart = parseProjectDate(project.start_date);
    const pEnd = parseProjectDate(project.end_date);
    if (!pStart) continue;
    const endDate = pEnd || pStart;
    if (endDate < weekStart || pStart > weekEnd) continue;
    const visibleStart = pStart < weekStart ? weekStart : pStart;
    const visibleEnd = endDate > weekEnd ? weekEnd : endDate;
    const leftDays = differenceInDays(visibleStart, weekStart);
    const spanDays = differenceInDays(visibleEnd, visibleStart) + 1;
    const leftPct = (leftDays / totalDays) * 100;
    const widthPct = (spanDays / totalDays) * 100;
    const label = `${format(visibleStart, 'd MMM')} – ${format(visibleEnd, 'd MMM')}`;
    const fullTooltip = `${project.name}\n${label}\n\nUnassigned: ${project.unassigned} · Outstanding: ${project.outstanding} · Confirmed: ${project.confirmed}`;
    rows.push({
      project,
      leftPct,
      widthPct,
      label,
      fullTooltip,
      barStyle: getCrewBarStyle(project),
      phaseBars: getCrewPhaseBars(project)
    });
  }
  return rows;
});

/** Linear month view: 30-day continuous window from calendarStart. */
// eslint-disable-next-line no-unused-vars -- reserved for month view UI
const linearMonthDays = computed(() => {
  if (viewMode.value !== 'month') return [];
  const start = startOfDay(calendarStart.value);
  const days = [];
  for (let i = 0; i < MONTH_VIEW_DAYS; i++) {
    const date = addDays(start, i);
    days.push({
      key: format(date, 'yyyy-MM-dd'),
      date,
      dayNum: format(date, 'd'),
      isToday: isSameDay(date, new Date())
    });
  }
  return days;
});

/** Linear month view: projects that overlap the 30-day window, with leftPct and widthPct for bar position. */
const linearMonthProjectRows = computed(() => {
  if (viewMode.value !== 'month') return [];
  const viewStart = startOfDay(calendarStart.value);
  const viewEnd = addDays(viewStart, MONTH_VIEW_DAYS - 1);
  const totalDays = MONTH_VIEW_DAYS;
  const rows = [];
  for (const project of displayedProjects.value) {
    const pStart = parseProjectDate(project.start_date);
    const pEnd = parseProjectDate(project.end_date);
    if (!pStart) continue;
    const endDate = pEnd || pStart;
    if (endDate < viewStart || pStart > viewEnd) continue;
    const visibleStart = pStart < viewStart ? viewStart : pStart;
    const visibleEnd = endDate > viewEnd ? viewEnd : endDate;
    const leftDays = differenceInDays(visibleStart, viewStart);
    const spanDays = differenceInDays(visibleEnd, visibleStart) + 1;
    const leftPct = (leftDays / totalDays) * 100;
    const widthPct = (spanDays / totalDays) * 100;
    const label = `${format(visibleStart, 'd MMM')} – ${format(visibleEnd, 'd MMM')}`;
    const fullTooltip = `${project.name}\n${label}\n\nUnassigned: ${project.unassigned} · Outstanding: ${project.outstanding} · Confirmed: ${project.confirmed}`;
    rows.push({
      project,
      leftPct,
      widthPct,
      label,
      fullTooltip,
      barStyle: getCrewBarStyle(project),
      phaseBars: getCrewPhaseBars(project)
    });
  }
  return rows;
});

/** Three-icon system (same as crew widget): unassigned / outstanding / confirmed. */
function getCrewIcons(project) {
  const u = project.unassigned ?? 0;
  const o = project.outstanding ?? 0;
  const c = project.confirmed ?? 0;
  return [
    { icon: 'pi-exclamation-triangle', count: u, label: 'Unassigned', tooltip: `Unassigned: ${u}`, slotClass: 'crew-slot-empty' },
    { icon: 'pi-clock', count: o, label: 'Outstanding', tooltip: `Outstanding: ${o}`, slotClass: 'crew-slot-requested' },
    { icon: 'pi-check-circle', count: c, label: 'Confirmed', tooltip: `Confirmed: ${c}`, slotClass: 'crew-slot-confirmed' }
  ];
}

/** Rows for TimelineView (month or week): same layout as projects timeline – status bar with crew phases glued to bottom + three icons. */
const timelineViewRows = computed(() => {
  const source = viewMode.value === 'week' ? linearWeekProjectRows.value : linearMonthProjectRows.value;
  return source.map((row) => ({
    id: row.project.id,
    label: row.project.name,
    leftPct: row.leftPct,
    widthPct: row.widthPct,
    barStyle: row.barStyle,
    tooltip: row.fullTooltip,
    phaseBars: row.phaseBars || [],
    crewIcons: getCrewIcons(row.project)
  }));
});

const viewRangeLabel = computed(() => {
  if (viewMode.value === 'month') {
    return format(calendarStart.value, 'MMMM yyyy');
  }
  if (viewMode.value === 'week') {
    const start = startOfWeek(calendarStart.value, { weekStartsOn: 1 });
    const end = endOfWeek(calendarStart.value, { weekStartsOn: 1 });
    return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
  }
  return format(calendarStart.value, 'EEEE, d MMMM yyyy');
});

// eslint-disable-next-line no-unused-vars -- used by timeline title when view is month
function formatMonthTitle(date) {
  const start = startOfDay(date);
  const end = addDays(start, MONTH_VIEW_DAYS - 1);
  return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
}

function formatDayFull(date) {
  return format(date, 'EEEE, d MMMM yyyy');
}

function onViewModeChange() {
  // When switching view, show the period that contains today
  const today = new Date();
  if (viewMode.value === 'month') {
    calendarStart.value = startOfDay(today);
  } else if (viewMode.value === 'week') {
    calendarStart.value = startOfWeek(today, { weekStartsOn: 1 });
  } else if (viewMode.value === 'day') {
    calendarStart.value = new Date(today);
  }
  loadProjects();
}

function goBackward() {
  if (viewMode.value === 'month') {
    calendarStart.value = subDays(calendarStart.value, MONTH_VIEW_DAYS);
  } else if (viewMode.value === 'week') {
    calendarStart.value = subWeeks(calendarStart.value, 1);
  } else {
    calendarStart.value = subDays(calendarStart.value, 1);
  }
  loadProjects();
}

function goForward() {
  if (viewMode.value === 'month') {
    calendarStart.value = addDays(calendarStart.value, MONTH_VIEW_DAYS);
  } else if (viewMode.value === 'week') {
    calendarStart.value = addWeeks(calendarStart.value, 1);
  } else {
    calendarStart.value = addDays(calendarStart.value, 1);
  }
  loadProjects();
}

function goToToday() {
  const today = new Date();
  if (viewMode.value === 'month') {
    calendarStart.value = startOfDay(today);
  } else if (viewMode.value === 'week') {
    calendarStart.value = startOfWeek(today, { weekStartsOn: 1 });
  } else {
    calendarStart.value = today;
  }
  loadProjects();
}

function goToProject(projectId) {
  router.push(`/project/${projectId}`);
}

function onCalendarUpdate(nextStart) {
  calendarStart.value = nextStart;
  loadProjects();
}

/** Get the visible date range (YYYY-MM-DD) for the current view and calendarStart. */
function getVisibleRange() {
  const start = calendarStart.value;
  let rangeStart;
  let rangeEnd;
  if (viewMode.value === 'month') {
    rangeStart = startOfDay(start);
    rangeEnd = addDays(rangeStart, MONTH_VIEW_DAYS - 1);
  } else if (viewMode.value === 'week') {
    rangeStart = startOfWeek(start, { weekStartsOn: 1 });
    rangeEnd = endOfWeek(start, { weekStartsOn: 1 });
  } else {
    rangeStart = rangeEnd = start;
  }
  return {
    startDate: format(rangeStart, 'yyyy-MM-dd'),
    endDate: format(rangeEnd, 'yyyy-MM-dd')
  };
}

async function loadProjects() {
  loading.value = true;
  error.value = null;
  const { startDate, endDate } = getVisibleRange();
  try {
    const data = await getCrewTimelineProjects(startDate, endDate);
    projects.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('[CrewingComponent] Failed to load crew timeline projects:', err);
    error.value = err?.response?.data?.message ?? err?.message ?? 'Failed to load projects';
    projects.value = [];
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  if (viewMode.value === 'month') {
    // Bootstrap: fetch wide range to find earliest job, then show 30 days from there
    const today = new Date();
    const wideStart = format(subDays(today, 30), 'yyyy-MM-dd');
    const wideEnd = format(addDays(today, 180), 'yyyy-MM-dd');
    loading.value = true;
    error.value = null;
    try {
      const data = await getCrewTimelineProjects(wideStart, wideEnd);
      const list = Array.isArray(data) ? data : [];
      projects.value = list;
      // Find earliest project start; default to today if none
      let earliest = null;
      for (const p of list) {
        const d = parseProjectDate(p.start_date);
        if (d && (!earliest || d < earliest)) earliest = d;
      }
      calendarStart.value = earliest ? startOfDay(earliest) : startOfDay(today);
    } catch (err) {
      console.error('[CrewingComponent] Failed to load crew timeline projects:', err);
      error.value = err?.response?.data?.message ?? err?.message ?? 'Failed to load projects';
      projects.value = [];
      calendarStart.value = startOfDay(today);
    } finally {
      loading.value = false;
    }
  } else {
    calendarStart.value = viewMode.value === 'week'
      ? startOfWeek(new Date(), { weekStartsOn: 1 })
      : new Date();
    loadProjects();
  }
});
</script>

<style scoped>
.crewing-timeline {
  min-height: 400px;
}

.crew-project-card {
  border-color: var(--p-border-color);
}

/* Linear month view: ensure track has enough width for ~31 days */
.linear-month-axis-days {
  min-width: 400px;
}

.linear-month-track {
  min-width: 400px;
}

.linear-month-bar {
  top: 4px;
  bottom: 4px;
}
</style>
